// ═══════════════════════════════════════════════════════════
// 设置面板 — API 源选择、自定义接口
// ═══════════════════════════════════════════════════════════

import { storage } from '../lib/storage.js';
import { getActiveSites, getSiteName } from '../config/sites.js';
import { TIMEOUT } from '../config/constants.js';
import { showToast } from './toast.js';
import { testSiteAvailability } from '../lib/http.js';

let currentSource = storage.apiSource;

export function initSettings() {
    currentSource = storage.apiSource;

    // 动态生成源选项
    const select = document.getElementById('apiSource');
    if (select) {
        const activeSites = getActiveSites();
        select.innerHTML = activeSites
            .map(s => `<option value="${s.key}">${s.name}${s.desc ? ' (' + s.desc + ')' : ''}</option>`)
            .join('') + '<option value="custom">自定义接口</option>';

        select.value = currentSource;
    }

    // 显示自定义接口输入框
    if (currentSource === 'custom') {
        const input = document.getElementById('customApiInput');
        if (input) input.classList.remove('hidden');
        const urlInput = document.getElementById('customApiUrl');
        if (urlInput) urlInput.value = storage.customApiUrl;
    }

    updateCurrentCodeDisplay();

    // 初始状态测试
    if (currentSource !== 'custom') {
        updateSiteStatusWithTest(currentSource);
    } else {
        updateStatusText('待测试', 'status-dot testing');
    }

    // 绑定事件
    bindSettingsEvents();
}

function bindSettingsEvents() {
    const select = document.getElementById('apiSource');
    if (!select) return;

    select.addEventListener('change', handleSourceChange);

    const customUrlInput = document.getElementById('customApiUrl');
    if (customUrlInput) {
        customUrlInput.addEventListener('blur', handleCustomUrlBlur);
    }

    // 点击外部关闭设置面板
    document.addEventListener('click', (e) => {
        const panel = document.getElementById('settingsPanel');
        const btn = document.querySelector('[data-action="toggle-settings"]');
        if (panel && !panel.contains(e.target) && (!btn || !btn.contains(e.target))) {
            if (panel.classList.contains('show')) {
                panel.classList.remove('show');
            }
        }
    });
}

async function handleSourceChange(e) {
    currentSource = e.target.value;
    const customApiInput = document.getElementById('customApiInput');

    if (currentSource === 'custom') {
        if (customApiInput) customApiInput.classList.remove('hidden');
        updateStatusText('待测试', 'status-dot testing');
    } else {
        if (customApiInput) customApiInput.classList.add('hidden');
        showToast('正在测试站点可用性...', 'info');
        await updateSiteStatusWithTest(currentSource);
    }

    storage.apiSource = currentSource;
    updateCurrentCodeDisplay();

    // 重置搜索区域
    resetSearchArea();
}

async function handleCustomUrlBlur(e) {
    const url = e.target.value;
    storage.customApiUrl = url;

    if (currentSource === 'custom' && url) {
        showToast('正在测试接口可用性...', 'info');
        const isAvailable = await testSiteAvailability('custom', url);
        updateSiteStatusDisplay(isAvailable);

        if (!isAvailable) {
            showToast('接口不可用，请检查地址是否正确', 'error');
        } else {
            showToast('接口可用', 'success');
        }
    }
}

async function updateSiteStatusWithTest(source) {
    updateStatusText('测试中...', 'status-dot testing');
    const customUrl = source === 'custom' ? storage.customApiUrl : '';

    // 检查缓存
    const cached = storage.getSiteStatus(source, customUrl);
    if (cached !== null) {
        updateSiteStatusDisplay(cached);
        return;
    }

    try {
        const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('测试超时')), 8000)
        );
        const testPromise = testSiteAvailability(source, customUrl);
        const isAvailable = await Promise.race([testPromise, timeoutPromise]);

        updateSiteStatusDisplay(isAvailable);
        storage.setSiteStatus(source, customUrl, isAvailable);
    } catch {
        updateSiteStatusDisplay(false);
    }
}

function updateSiteStatusDisplay(isAvailable) {
    const el = document.getElementById('siteStatus');
    if (!el) return;
    if (isAvailable) {
        el.innerHTML = `<span class="status-dot online">●</span> 可用`;
    } else {
        el.innerHTML = `<span class="status-dot offline">●</span> 不可用`;
    }
}

function updateStatusText(text, colorClass, dot = '●') {
    const el = document.getElementById('siteStatus');
    if (el) el.innerHTML = `<span class="${colorClass}">${dot}</span> ${text}`;
}

function updateCurrentCodeDisplay() {
    const el = document.getElementById('currentCode');
    if (el) el.textContent = currentSource;
}

function resetSearchArea() {
    const results = document.getElementById('results');
    if (results) results.innerHTML = '';
    const searchInput = document.getElementById('searchInput');
    if (searchInput) searchInput.value = '';
    const resultsCount = document.getElementById('resultsCount');
    if (resultsCount) resultsCount.textContent = '';

    const searchArea = document.getElementById('searchArea');
    if (searchArea) {
        searchArea.classList.add('flex-1');
        searchArea.classList.remove('mb-8');
    }
    const resultsArea = document.getElementById('resultsArea');
    if (resultsArea) resultsArea.classList.add('hidden');

    const footer = document.querySelector('.footer');
    if (footer) footer.style.position = '';
}

// 导出切换设置面板（供 HTML onclick 使用）
export function toggleSettings(e) {
    if (e) e.stopPropagation();
    const panel = document.getElementById('settingsPanel');
    if (panel) panel.classList.toggle('show');
}

// 全局暴露
window.toggleSettings = toggleSettings;
window.getCurrentSource = () => currentSource;
window.getCustomApiUrl = () => {
    const input = document.getElementById('customApiUrl');
    return input ? input.value : storage.customApiUrl;
};
